import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useMenuPermission } from '@src/recoil/permission';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BottomPopupType, Categories, MenuManageTabProps, Product, PRODUCT_KEYS, Products } from '@type/productType';
import { CallGetCategories, CallGetProducts } from '@library/request/callProduct';
import { MenuEmptyBox, NoAuthBox } from '@src/pages/product/componnents/common';
import { useRecoilState } from 'recoil';
import { menuOrderGategoryId, menuScroll } from '@src/recoil/menu';

const typeOfTitle: Record<any, string> = {
  category: '카테고리',
  menu: '메뉴',
  optionGroup: '옵션 그룹',
};

const MenuOrder = (props: MenuManageTabProps) => {
  const { storeId, setLoading } = props;
  const navigate = useNavigate();
  const { menuPermission, isNoAuthOrder } = useMenuPermission(storeId);

  const [type, setType] = useState<BottomPopupType>('category'); // 카테고리 or 메뉴
  const [selectedCategory, setSelectedCategroy] = useState<Categories>(); // 선택된 메뉴

  const selectedProduct = useRef({ id: null, name: '' });
  const prevStoreId = useRef(storeId);

  const [currentCategoryId, setCategoryId] = useRecoilState(menuOrderGategoryId);
  const [menuScrollValue, setMenuScrollValue] = useRecoilState(menuScroll);

  // 카테고리 리스트 조회 API
  const { data: categoryList, isFetched } = useQuery([PRODUCT_KEYS.CATEGORY, storeId], {
    enabled: !!storeId && menuPermission.isFetch && (!menuPermission.data.length || !isNoAuthOrder),
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetCategories(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 옵션그룹 조회 API
  const { data: productDetail } = useQuery<Products>([PRODUCT_KEYS.PRODUCT, storeId, selectedProduct?.current?.id], {
    enabled: !!isFetched && !!storeId && !!selectedProduct?.current?.id,
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetProducts(storeId, selectedProduct?.current?.id);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  const onClickBtn = () => {
    switch (type) {
      case 'category':
        return navigate(`/product/sort?storeID=${storeId}`);
      case 'menu':
        return navigate(`/product/sort?storeID=${storeId}&categoryID=${selectedCategory?.categoryID}`);
      case 'optionGroup':
        return navigate(
          `/product/sort?storeID=${storeId}&categoryID=${selectedCategory?.categoryID}&productID=${selectedProduct?.current.id}`,
        );
    }
  };

  const ListElement = ({ list }: { list: (Categories | Product)[] }) => {
    const isCategory = (item: Categories | Product): item is Categories => {
      return (item as Categories).categoryID !== undefined;
    };

    return (
      <ul>
        {list.map((item) => {
          const id = isCategory(item) ? item.categoryID : item.productID;
          const title = isCategory(item) ? item.categoryName : item.name;
          const hasProduct = isCategory(item) && item.products.length > 0;

          return (
            <li key={id}>
              <div className="item-product" data-product-id={id}>
                <button
                  type="button"
                  className="txt-product"
                  onClick={() => {
                    if (isCategory(item)) {
                      if (hasProduct) {
                        setType('menu');
                        setMenuScrollValue({
                          depth1: window.scrollY,
                          depth2: 0,
                        });
                        const clickedMenu = categoryList.find((list) => list.categoryID === id);
                        setSelectedCategroy(clickedMenu);
                        setCategoryId({ type: 'menu', id: clickedMenu.categoryID, title: '' });
                      }
                    } else {
                      setType('optionGroup');
                      setMenuScrollValue({
                        depth1: menuScrollValue.depth1,
                        depth2: window.scrollY,
                      });
                      selectedProduct.current = { id, name: title };
                      setCategoryId({ type: 'optionGroup', id, title });
                    }
                  }}
                >
                  {title}
                </button>
                {(hasProduct || !isCategory(item)) && <span className="ico-arr" />}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const hasButtonVisible = useMemo(() => {
    if (type === 'category') return !!categoryList;
    if (type === 'menu') return !!selectedCategory;
    if (type === 'optionGroup') {
      return productDetail?.options?.some((opt) => Number(opt.isPriceOption) !== 1);
    }
    return false;
  }, [type, categoryList, selectedCategory, productDetail]);

  useEffect(() => {
    if (prevStoreId.current !== storeId) {
      setType('category');
      setCategoryId({ type: '', id: '', title: '' });
      selectedProduct.current = { id: null, name: '' };
    }
  }, [storeId]);

  useEffect(() => {
    if (!isFetched) return;
    if (currentCategoryId.id) {
      if (currentCategoryId.type === 'menu') {
        setSelectedCategroy(categoryList.find((list) => list.categoryID === currentCategoryId.id));
        setType('menu');
      } else if (currentCategoryId.type === 'optionGroup') {
        selectedProduct.current = { id: currentCategoryId.id, name: currentCategoryId.title };
        setType('optionGroup');
      }
    }
  }, [isFetched]);

  useEffect(() => {
    if (type === 'menu' && selectedCategory?.categoryID) {
      // 옵션그룹순서 탭에서 뒤로가기 아이콘 클릭하여 메뉴순서탭으로 이동시 데이터 세팅
      setCategoryId({ type: 'menu', id: selectedCategory?.categoryID, title: '' });
    }
  }, [type]);

  useEffect(() => {
    if (type === 'category' || menuScrollValue.depth1 > 0) {
      window.scrollTo(0, menuScrollValue.depth1);
    }
    if (type === 'menu' || menuScrollValue.depth2 > 0) {
      window.scrollTo(0, menuScrollValue.depth2);
    }
  }, [type]);

  useEffect(() => {
    // 순서 수정 후 변경된 순서로 반영
    if (!isFetched || currentCategoryId.type !== 'menu' || !currentCategoryId.id) return;
    setSelectedCategroy(categoryList.find((list) => list.categoryID === currentCategoryId.id));
  }, [isFetched, currentCategoryId, categoryList]);

  return (
    <div className="menu-order">
      {isNoAuthOrder ? (
        <NoAuthBox type="order" />
      ) : (
        <>
          {type === 'category' && (
            // 카테고리
            <div className="box-content">
              {categoryList ? <ListElement list={categoryList} /> : <MenuEmptyBox type="order" />}
            </div>
          )}
          {type === 'menu' && (
            // 메뉴
            <div className="box-content">
              <h4 className="tit-product">
                <button
                  type="button"
                  className="link-back"
                  onClick={() => {
                    setType('category');
                    setCategoryId({ type: '', id: '', title: '' });
                  }}
                >
                  <span className="ico-arr" />
                </button>
                <span className="category-name">{selectedCategory?.categoryName}</span>
              </h4>
              <div className="box-content">
                {selectedCategory ? <ListElement list={selectedCategory?.products} /> : <MenuEmptyBox type="order" />}
              </div>
            </div>
          )}
          {type === 'optionGroup' && (
            // 옵션 그룹
            <div className="box-content">
              <h4 className="tit-product">
                <button
                  type="button"
                  className="link-back"
                  onClick={() => {
                    setType('menu');
                    if (currentCategoryId.id) {
                      const item = categoryList.find((v) =>
                        v.products.find((i) => {
                          return i.productID === currentCategoryId.id;
                        }),
                      );
                      setSelectedCategroy(categoryList.find((list) => list.categoryID === item?.categoryID));
                    }
                    selectedProduct.current = { id: null, name: '' };
                  }}
                >
                  <span className="ico-arr" />
                </button>
                <span className="category-name">{selectedProduct?.current?.name}</span>
              </h4>
              <div className="box-content">
                {productDetail?.options?.filter((list) => Number(list.isPriceOption) !== 1).length > 0 ? (
                  <ul>
                    {productDetail.options
                      .filter((list) => Number(list.isPriceOption) !== 1)
                      .map((list) => {
                        const { title, optionGroupID } = list;
                        return (
                          <li className="opt-group-li" key={optionGroupID}>
                            {title}
                          </li>
                        );
                      })}
                  </ul>
                ) : (
                  <MenuEmptyBox type="optionGroup" isVisibleSubTitle={false} />
                )}
              </div>
            </div>
          )}
          {hasButtonVisible && (
            <div className="fixedBtnGroup">
              <button type="button" id="btnSubmit" className="btnDone" onClick={onClickBtn}>
                {typeOfTitle[type]} 순서 수정
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MenuOrder;
